import { type ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useBookableServices } from '@/hooks/usePublicBooking';
import { resolveClinicLogoUrl } from '@/lib/clinicBranding';
import { supabase } from '@/lib/supabase';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { PortalAuthShell, portalLoginCopy, resolvePortalGuestBookHref } from '@/components/portal/PortalAuthShell';
import { uiLocale } from '@/lib/i18n';

export function PortalProtectedRoute({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();
  const [hasPortalAccess, setHasPortalAccess] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);
  const copy = portalLoginCopy(uiLocale());
  const fromPath =
    (location.state as { from?: Location } | null)?.from?.pathname ?? location.pathname;
  const bookHref = resolvePortalGuestBookHref({
    search: location.search,
    fromPath,
  });
  const bookingSlug = bookHref?.replace(/^\/book\//, '') ?? null;
  const { data: bookingClinic } = useBookableServices(bookingSlug);
  const clinicName = bookingClinic?.clinic_name ?? 'Clinio';
  const brandLogoUrl = resolveClinicLogoUrl(bookingClinic?.clinic_logo);

  useEffect(() => {
    if (status === 'authenticated' && user) {
      const checkPortalAccess = async () => {
        try {
          const { data: patients, error } = await supabase
            .from('patients')
            .select('id')
            .eq('portal_user_id', user.id)
            .eq('status', 'active')
            .limit(1);
          if (error) {
            setHasPortalAccess(false);
          } else {
            setHasPortalAccess(Boolean(patients && patients.length > 0));
          }
        } catch {
          setHasPortalAccess(false);
        } finally {
          setChecking(false);
        }
      };
      void checkPortalAccess();
    } else if (status === 'unauthenticated') {
      setChecking(false);
    }
  }, [status, user]);

  if (status === 'initializing' || (status === 'authenticated' && checking)) {
    return (
      <div className="pt">
        <div className="auth">
          <main className="auth-main">
            <div className="help" style={{ textAlign: 'center', width: '100%' }}>
              Indlæser…
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/portal/login" state={{ from: location }} replace />;
  }

  if (hasPortalAccess === false) {
    const email = user?.email ?? '';
    return (
      <PortalAuthShell
        clinicName={clinicName}
        logoUrl={brandLogoUrl}
        bookHref={bookHref}
        bookLabel={copy.book}
      >
        <div className="card auth-card">
          <div className="big-ic" style={{ background: 'var(--amber-tint)', color: 'var(--amber-ink)' }}>
            <PortalIcon name="searchOff" />
          </div>
          <h1 style={{ fontSize: 22, lineHeight: '30px', fontWeight: 600, marginTop: 16, textAlign: 'center' }}>
            {copy.noAccessTitle}
          </h1>
          <p className="muted" style={{ marginTop: 8, textAlign: 'center' }}>
            {copy.noAccessBody(email)}
          </p>
          <div className="notice neutral" style={{ marginTop: 20, flexDirection: 'column', gap: 6 }}>
            <b style={{ fontWeight: 600 }}>{copy.noAccessReasonsTitle}</b>
            <ul style={{ listStyle: 'disc', paddingLeft: 20, color: 'var(--muted)' }}>
              <li>{copy.noAccessReason1}</li>
              <li>{copy.noAccessReason2}</li>
            </ul>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
            <button
              type="button"
              className="btn btn-primary btn-block btn-lg"
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = '/portal/login';
              }}
            >
              <PortalIcon name="logout" size="sm" />
              {copy.signOutOther}
            </button>
          </div>
        </div>
      </PortalAuthShell>
    );
  }

  return <>{children}</>;
}
